import React, { useState } from "react";
import {
  Box,
  TextField,
  Typography,
  Paper,
  Grid,
  InputAdornment,
  Button,
  CircularProgress,
} from "@mui/material";
import { AccountCircle, Email, Phone } from "@mui/icons-material";
import { FormDatas } from "../../types/FormData";
import { useSnackbar } from "src/contexts/SnackbarContext";

interface ContactInformationProps {
  formData: FormDatas;
  setFormData: (data: FormDatas) => void;
  onSubmit: () => Promise<void>; // Callback passed from MultiStepForm
}

const ContactInformation: React.FC<ContactInformationProps> = ({
  formData,
  setFormData,
  onSubmit,
}) => {
  const [loading, setLoading] = useState(false);
  const { showMessage } = useSnackbar();

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [event.target.name]: event.target.value,
    });
  };

  const validateForm = (): boolean => {
    if (!formData.firstName || !formData.lastName || !formData.phoneNumber || !formData.email) {
      showMessage("Bitte füllen Sie alle Felder aus.", "error");
      return false;
    }
    if (!validateEmail(formData.email)) {
      showMessage("Bitte geben Sie eine gültige E-Mail-Adresse ein.", "error");
      return false;
    }
    return true;
  };

  const validateEmail = (email: string): boolean => {
    const regex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;
    return regex.test(email);
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setLoading(true);
    try {
      // Call the parent’s submission function
      await onSubmit();
    } catch (error: any) {
      showMessage("Fehler beim Absenden des Formulars. Bitte versuchen Sie es erneut.", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ textAlign: "center", mt: 4, display: "flex", justifyContent: "center" }}>
      <Paper
        elevation={3}
        sx={{
          p: 5,
          borderRadius: "12px",
          width: "100%",
          maxWidth: "600px",
          boxShadow: "0px 4px 15px rgba(0, 0, 0, 0.1)",
        }}
      >
        <Typography variant="h3" sx={{ fontWeight: "bold", color: "primary.main", mb: 1 }}>
          Wir freuen uns, Sie kennenzulernen!
        </Typography>
        <Typography variant="body1" sx={{ color: "#5f6368", mb: 4 }}>
          Bitte geben Sie Ihre Kontaktdaten ein, damit wir Sie erreichen können.
        </Typography>

        <Grid container spacing={3}>
          {/* Vorname */}
          <Grid item xs={12}>
            <Typography sx={{ textAlign: "left", fontWeight: "bold", mb: 0.5 }}>
              Ihr Vorname
            </Typography>
            <TextField
              fullWidth
              placeholder="Vorname"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              variant="outlined"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <AccountCircle color="primary" />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  "&:hover fieldset": { borderColor: "primary.main" },
                  "&.Mui-focused fieldset": { borderColor: "primary.main" },
                },
              }}
            />
          </Grid>

          {/* Nachname */}
          <Grid item xs={12}>
            <Typography sx={{ textAlign: "left", fontWeight: "bold", mb: 0.5 }}>
              Ihr Nachname
            </Typography>
            <TextField
              fullWidth
              placeholder="Nachname"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              variant="outlined"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <AccountCircle color="primary" />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  "&:hover fieldset": { borderColor: "primary.main" },
                  "&.Mui-focused fieldset": { borderColor: "primary.main" },
                },
              }}
            />
          </Grid>

          {/* Telefonnummer */}
          <Grid item xs={12}>
            <Typography sx={{ textAlign: "left", fontWeight: "bold", mb: 0.5 }}>
              Telefonnummer
            </Typography>
            <TextField
              fullWidth
              placeholder="Telefonnummer"
              name="phoneNumber"
              value={formData.phoneNumber}
              onChange={handleChange}
              variant="outlined"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Phone color="primary" />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  "&:hover fieldset": { borderColor: "primary.main" },
                  "&.Mui-focused fieldset": { borderColor: "primary.main" },
                },
              }}
            />
          </Grid>

          {/* E-Mail */}
          <Grid item xs={12}>
            <Typography sx={{ textAlign: "left", fontWeight: "bold", mb: 0.5 }}>
              Ihre E-Mail-Adresse
            </Typography>
            <TextField
              fullWidth
              placeholder="E-Mail"
              name="email"
              value={formData.email}
              onChange={handleChange}
              variant="outlined"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email color="primary" />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  "&:hover fieldset": { borderColor: "primary.main" },
                  "&.Mui-focused fieldset": { borderColor: "primary.main" },
                },
              }}
            />
          </Grid>
        </Grid>

        <Button
          variant="contained"
          color="primary"
          sx={{ mt: 3, width: "100%" }}
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? <CircularProgress size={24} /> : "einreichen"}
        </Button>
      </Paper>
    </Box>
  );
};

export default ContactInformation;
